<template>
  <div class="m-3" style="height: 90vh">
    <div class="grid grid-cols-12 items-center content-center gap-2">
      <el-select class="input-label col-span-12 md:col-span-2"
                 v-model="selectedValue">
        <el-option key="quickView"
                   :label="$t('views.common.listView.quickView')"
                   value="quickView">
        </el-option>
        <el-option key="allItems" :label="$t('views.common.listView.allItems')"
                   value="allItems">
        </el-option>
        <el-option
            key="deletedItems"
            :label="$t('views.common.listView.deletedItems')"
            value="deletedItems"
        >
        </el-option>
        <el-option
            key="export"
            :label="$t('views.common.listView.selectedRows.export')"
            value="export"
        >
        </el-option>

        <el-option
            v-if="selectedValue != 'deletedItems'"
            key="delete"
            :label="$t('views.common.listView.selectedRows.delete')"
            value="delete"
        >
        </el-option>
        <el-option
            v-if="selectedValue == 'deletedItems'"
            key="restore"
            :label="$t('views.common.listView.selectedRows.restore')"
            value="restore"
        >
        </el-option>
      </el-select>

      <el-input
          :placeholder="$t('views.common.listView.searchRows')"
          class="w-full col-span-12 md:col-span-6"
          :prefix-icon="Search"
          v-model="searchTerm"
      />

      <div class="col-span-12 md:col-span-4 flex items-center gap-4">
        <ToggleGroup v-model="appointmentsFilter"
                     class="gap-2 flex-wrap lg:flex-nowrap" type="single">
          <ToggleGroupItem value="old_appointments"
                           class="data-[state=on]:text-orange-600 bg-transparent p-1">
            <Icon icon="prime:history" width="24" height="24"/>
          </ToggleGroupItem>
          <ToggleGroupItem value="checkin"
                           class="data-[state=on]:text-green-600 bg-transparent p-1">
            <Icon icon="cuida:checkbox-checked-outlined" width="24"
                  height="24"/>
          </ToggleGroupItem>
        </ToggleGroup>

        <button @click="() => {$router.push($Route.CUSTOMERS_QUEUE)}"
                class="rounded-xl border border-input bg-input p-1 text-sm ring-offset-background focus:outline-none focus:ring-1 focus:ring-ring focus:ring-offset-0">
          <Icon icon="ic:twotone-monitor" width="24" height="24"/>
        </button>
        <horizontal-button
            :title="$t('views.appointments.newAppointment')"
            @click.prevent="$router.push($Route.APPOINTMENTS_NEW_APPOINTMENT)"
            v-if="$can('create', '/customer/appointments')"
        />
      </div>
    </div>

    <ag-grid-vue
        v-if="rowModelType"
        class="ag-theme-alpine mt-4 md:mt-0"
        :columnDefs="columnDefs"
        rowSelection="multiple"
        :enable-rtl="$ctx.getDir() === 'rtl'"
        style="height: 85vh"
        @cell-double-clicked="onCellDoubleClicked"
        :defaultColDef="defaultColDef"
        :pagination="selectedValue == 'allItems'"
        :paginationAutoPageSize="selectedValue == 'allItems'"
        :onGridReady="onGridReady"
        :rowBuffer="rowBuffer"
        :rowModelType="rowModelType"
        :cacheBlockSize="cacheBlockSize"
        :cacheOverflowSize="cacheOverflowSize"
        :maxConcurrentDatasourceRequests="maxConcurrentDatasourceRequests"
        :infiniteInitialRowCount="infiniteInitialRowCount"
        :maxBlocksInCache="maxBlocksInCache"
    >
    </ag-grid-vue>

    <div class="mt-2">{{ $t('views.common.listView.count') }}: {{ count }}</div>
  </div>
</template>

<script lang="ts">
import {defineComponent, onBeforeMount, ref} from 'vue'

// components
import {AgGridVue} from 'ag-grid-vue3'
import {
  ColDef,
  GridApi,
  GridOptions,
  IGetRowsParams,
  InfiniteRowModelModule,
  ModuleRegistry
} from 'ag-grid-community'
import HorizontalButton from '@/components/HorizontalButton.vue'
import {Icon} from '@iconify/vue';

import Appointment, {
  appointmentI18nMessages,
  AppointmentPaginated
} from '@/models/customer/Appointment'
import {useI18n} from 'vue-i18n'
import {formatPostgresDate, parseDate} from '@/utils/date'
import {customerI18nMessages} from '@/models/customer/Customer'
import {Search} from '@element-plus/icons-vue'
import {ToggleGroup, ToggleGroupItem} from "@/components/ui/toggle-group";
import AppointmentsCellRenderer
  from "@/views/appointments/AppointmentsCellRenderer.vue";
import dayjs from "dayjs";
import ContactedCellRenderer
  from "@/views/examinations/ContactedCellRenderer.vue";
import Examination from "@/models/customer/Examination";
import {useSettingsStore} from "@/stores/settingsStore";
import {petI18nMessages} from "@/models/Pet";

ModuleRegistry.registerModules([InfiniteRowModelModule])

export default defineComponent({
  name: 'Appointments',

  setup() {
    const {t} = useI18n({
      messages: {
        en: {
          ...customerI18nMessages.en,
          ...appointmentI18nMessages.en,
          ...petI18nMessages.en,
          contacted: 'Contacted?',
          vet: 'Vet',
          service: 'Service'
        },
        ar: {
          ...customerI18nMessages.ar,
          ...appointmentI18nMessages.ar,
          ...petI18nMessages.ar,
          contacted: 'تم التواصل؟',
          vet: 'الطبيب البيطري',
          service: 'الخدمة'
        }
      }
    })

    return {t, Search, useSettingsStore}
  },

  data() {
    const columnDefs: ColDef[] = []
    const rowData: Appointment[] = []
    const gridOptions: GridOptions = {}
    const defaultColDef: ColDef = {}

    const rowBuffer = ref<any>(null)
    const rowSelection = ref<any>(null)
    const rowModelType = ref<any>(null)
    const cacheBlockSize = ref<any>(null)
    const cacheOverflowSize = ref<any>(null)
    const maxConcurrentDatasourceRequests = ref<any>(null)
    const infiniteInitialRowCount = ref<any>(null)
    const maxBlocksInCache = ref<any>(null)
    const appointmentsFilter = ref<string>('')

    onBeforeMount(() => {
      rowBuffer.value = 0
      rowSelection.value = 'multiple'
      rowModelType.value = 'infinite'
      cacheBlockSize.value = 100
      cacheOverflowSize.value = 100
      maxConcurrentDatasourceRequests.value = 2
      infiniteInitialRowCount.value = 0
      maxBlocksInCache.value = 10
    })

    return {
      columnDefs,
      rowData,
      gridOptions,
      defaultColDef,

      searchTerm: '',
      selectedValue: 'quickView',
      gridApi: undefined as GridApi | undefined,
      rowBuffer,
      rowSelection,
      rowModelType,
      cacheBlockSize,
      cacheOverflowSize,
      maxConcurrentDatasourceRequests,
      infiniteInitialRowCount,
      maxBlocksInCache,
      count: 0,
      appointmentsFilter: appointmentsFilter
    }
  },

  components: {
    ToggleGroup,
    AgGridVue,
    HorizontalButton,
    ToggleGroupItem,
    Icon
  },

  async beforeMount() {
    this.defaultColDef = {
      sortable: false,
      filter: false,
      resizable: true,
      minWidth: 100
    }

    this.columnDefs = [
      {
        field: '-',
        hide: true,
        lockVisible: true,
        filter: 'agTextColumnFilter',
        filterParams: {
          newRowsAction: 'keep'
        }
      },
      {
        headerName: `${this.t('appointment')} #`,
        field: 'externalID',
        checkboxSelection: true,
        headerCheckboxSelection: true,
        width: 130,
        cellStyle: ({ value, data }) => {
          const base = { color: 'black' };
          if (!value) return base;

          if (data.checkinDate) {
            base.color = 'green'; // Appointments within 24 hours
            return  base;
          }
        }
      },
      {
        headerName: this.t('models.common.date'),
        field: 'appointmentDate',
        width: 120,
        cellStyle: ({ value, data }) => {
          const base = { color: 'black' };
          if (!value) return base;

          if (data.checkinDate) {
            base.color = 'green'; // Appointments within 24 hours
            return  base;
          }

          const appointmentDate = new Date(data.appointmentDate);
          const now = new Date();
          appointmentDate.setHours(0, 0, 0, 0);
          now.setHours(0, 0, 0, 0);


          const timeDiff = appointmentDate - now;
          const hoursDiff = timeDiff / (1000 * 60 * 60); // Convert milliseconds to hours

          if (hoursDiff <= 72 && hoursDiff >= 24) {
            base.color = 'purple'; // Appointments within 3 days (but more than 24 hours)
          } else if (hoursDiff <= 24 && hoursDiff >= 0) {
            base.color = 'orange'; // Appointments within 24 hours
          }

          return base;
        },
        valueGetter: (params) => {
          if (!params.data) return '';
          const Appointment = params.node?.data as Appointment;
          if (Appointment.appointmentDate) {
            return formatPostgresDate(String(Appointment.appointmentDate), 'DD/MM/YYYY');
          }
          return '';
        }
      },
      {
        headerName: this.t('models.common.time'),
        field: 'createdAt',
        cellStyle: {direction: 'ltr !important', textAlign: 'center'},
        valueGetter: (params) => {
          if (!params.data) return ''
          const appointment = params.node?.data as Appointment
          if (appointment.appointmentDate) {
            return formatPostgresDate(String(appointment.appointmentDate), 'hh:mm A')
          }
          return ''
        },
        width: 100,
        cellStyle: ({ value, data }) => {
          const base = { color: 'black' };
          if (!value) return base;

          if (data.checkinDate) {
            base.color = 'green'; // Appointments within 24 hours
            return  base;
          }
        }

      },
      ...(useSettingsStore().getValue('vet_module') == 'true' ? [{
        headerName: this.t('pet'),
        field: 'pet',
        width: 120,
        valueGetter: (params) => {
          if (!params.data) return ''
          const appointment = params.node?.data as Appointment
          if (appointment.pet) {
            return `${appointment.pet?.name}`
          }
          return ''
        },
        cellStyle: ({ value, data }) => {
          const base = { color: 'black' };
          if (!value) return base;

          if (data.checkinDate) {
            base.color = 'green';
            return  base;
          }
        }
      }] : []),
      {
        headerName: this.t('customer'),
        field: 'customer',
        width: 150,
        valueGetter: (params) => {
          if (!params.data) return ''
          const appointment = params.node?.data as Appointment
          if (appointment.customer) {
            return `${appointment.customer?.name}`
          }
          return ''
        },
        cellStyle: ({ value, data }) => {
          const base = { color: 'black' };
          if (!value) return base;

          if (data.checkinDate) {
            base.color = 'green'; // Appointments within 24 hours
            return  base;
          }
        }
      },
      {
        headerName: this.t('phone'),
        field: 'customer',
        width: 120,
        valueGetter: (params) => {
          if (!params.data) return ''
          const appointment = params.node?.data as Appointment
          if (appointment.customer) {
            return `${appointment.customer?.phone}`
          }
          return ''
        },
        cellStyle: ({ value, data }) => {
          const base = { color: 'black' };
          if (!value) return base;

          if (data.checkinDate) {
            base.color = 'green'; // Appointments within 24 hours
            return  base;
          }
        }
      },
      {
        headerName: this.t('setBy'),
        field: 'user',
        valueGetter: (params) => {
          if (!params.data) return ''
          const appointment = params.node?.data as Appointment
          if (appointment.user) {
            return `${appointment.user?.name}`
          }
          return ''
        },
        width: 100,
        cellStyle: ({ value, data }) => {
          const base = { color: 'black' };
          if (!value) return base;

          if (data.checkinDate) {
            base.color = 'green'; // Appointments within 24 hours
            return  base;
          }
        }
      },
      {
        headerName: useSettingsStore().getValue('vet_module') == 'true' ? this.t('vet') : this.t('optometrist'),
        field: 'optometrist',
        valueGetter: (params) => {
          if (!params.data) return ''
          const appointment = params.node?.data as Appointment
          if (appointment.optometrist) {
            return `${appointment.optometrist?.name}`
          }
          return ''
        },
        width: 110,
        cellStyle: ({ value, data }) => {
          const base = { color: 'black' };
          if (!value) return base;

          if (data.checkinDate) {
            base.color = 'green'; // Appointments within 24 hours
            return  base;
          }
        }
      },
      ...(useSettingsStore().getValue('vet_module') == 'true' ? [{
        headerName: this.t('service'),
        field: 'serviceItem',
        width: 150,
        valueGetter: (params) => {
          if (!params.data) return ''
          const appointment = params.node?.data as Appointment
          if (appointment.serviceItem) {
            return `${appointment.serviceItem?.name}`
          }
          return ''
        },
        cellStyle: ({ value, data }) => {
          const base = { color: 'black' };
          if (!value) return base;

          if (data.checkinDate) {
            base.color = 'green';
            return  base;
          }
        }
      }] : []),
      {
        headerName: this.t('checkinDate'),
        field: 'checkinDate',
        cellStyle: {direction: 'ltr !important', textAlign: 'center'},
        valueGetter: (params) => {
          if (!params.data) return ''
          const appointment = params.node?.data as Appointment
          if (appointment.checkinDate) {
            return formatPostgresDate(String(appointment.checkinDate), 'DD/MM/YYYY hh:mm A')
          }
          return ''
        },
        cellStyle: ({ value, data }) => {
          const base = { color: 'black' };
          if (!value) return base;

          if (data.checkinDate) {
            base.color = 'green'; // Appointments within 24 hours
            return  base;
          }
        }
      },
      {
        headerName: this.t('notes'),
        field: 'notes',
        width: 100,
        valueGetter: (params) => {
          if (!params.data) return ''
          const appointment = params.node?.data as Appointment
          return `${appointment.notes}`
        },
        cellStyle: ({ value, data }) => {
          const base = { color: 'black' };
          if (!value) return base;

          if (data.checkinDate) {
            base.color = 'green'; // Appointments within 24 hours
            return  base;
          }
        }
      },
      {
        headerName: this.t('contacted'),
        cellRenderer: ContactedCellRenderer,
        cellRendererParams: () => ({
          appointmentsFilter: this.appointmentsFilter,
          handleCheckChange: this.handleCheckChange
        })
      },
      {
        cellRenderer: AppointmentsCellRenderer,
        cellRendererParams: () => ({
          appointmentsFilter: this.appointmentsFilter,
          reload: this.updateAppointmentFilter
        }),
      }
    ]

    try {
      this.count = (
          await this.$http.get<{
            count: number
          }>('/helpers/count?model=customers_appointments')
      ).count
    } catch (error) {
      this.$alertModal.showDanger({title: error.title, body: error.body})
    }
  },

  mounted() {
    // this.gridApi?.sizeColumnsToFit()
  },

  watch: {
    appointmentsFilter: {
      handler() {
        this.updateAppointmentFilter();
      },
    },
    searchTerm() {
      this.filterResult()
    },
    async selectedValue(newValue, oldValue) {
      if (oldValue == 'delete' || oldValue == 'export' || oldValue == 'restore') {
        return
      }

      if (
          this.selectedValue === 'delete' ||
          this.selectedValue === 'export' ||
          this.selectedValue === 'restore'
      ) {
        const selected = this.gridApi?.getSelectedRows()

        if (selected?.length) {
          if (this.selectedValue === 'delete') {
            // send request to archive
            await this.delete(selected)
            if (oldValue == 'quickView') {
              this.gridApi?.refreshInfiniteCache()
            } else {
              await this.updateRowData()
            }
          } else if (this.selectedValue === 'restore') {
            // send request to archive
            await this.restoreItems(selected)
            await this.updateRowData(oldValue == 'deletedItems')
          } else {
            this.gridApi?.exportDataAsCsv({onlySelected: true})
            this.$alertModal.showSuccess({
              title: this.$t('views.common.listView.selectedRows.exportSuccess')
            })
          }

          // deselect
          selected.length = 0
        }
        this.selectedValue = oldValue
      } else if (this.selectedValue == 'allItems') {
        this.rowModelType = null
        this.defaultColDef.sortable = true
        this.defaultColDef.filter = true
        setTimeout(() => {
          this.rowModelType = 'clientSide'
        }, 100)
      } else if (this.selectedValue == 'quickView') {
        this.rowModelType = null
        this.defaultColDef.sortable = false
        this.defaultColDef.filter = false
        setTimeout(() => {
          this.rowModelType = 'infinite'
        }, 100)
      } else if (this.selectedValue == 'deletedItems') {
        this.rowModelType = null
        this.defaultColDef.sortable = true
        this.defaultColDef.filter = true
        setTimeout(() => {
          this.rowModelType = 'clientSide'
        }, 100)
      }
    }
  },

  methods: {
    updateAppointmentFilter() {
      if (this.selectedValue == 'allItems') {
        this.rowModelType = null
        this.defaultColDef.sortable = true
        this.defaultColDef.filter = true
        setTimeout(() => {
          this.rowModelType = 'clientSide'
        }, 100)
      } else if (this.selectedValue == 'quickView') {
        this.rowModelType = null
        this.defaultColDef.sortable = false
        this.defaultColDef.filter = false
        setTimeout(() => {
          this.rowModelType = 'infinite'
        }, 100)
      }
    },
    async handleCheckChange(event: any, data: Appointment, updateCheck: any) {
      console.log(event, updateCheck)
      try {
        data.contacted = event;
        data.requestOnlyAppointmentDate = formatPostgresDate(data.appointmentDate)
        const url = `${Appointment.ENDPOINT}/${data.id}`
        const resp = await this.$http.put<Appointment>(url, data)
      } catch (error) {
        return error
      }
    },
    async updateRowData(softDeleted: boolean = false) {
      this.gridApi?.showLoadingOverlay()

      const urlParams = new URLSearchParams()
      if (this.appointmentsFilter) {
        urlParams.set('filter', this.appointmentsFilter)
      }

      try {
        const data = await this.$http.get<Appointment[]>(Appointment.ENDPOINT + `?${urlParams.toString()}`)
        if (softDeleted) {
          this.rowData = data
              .map((appt) => Appointment.from(appt))
              .filter((appt) => appt.deletedAt != null)
        } else {
          this.rowData = data
              .map((appt) => Appointment.from(appt))
              .filter((appt) => appt.deletedAt == null)
        }
        this.gridApi?.setGridOption('rowData', this.rowData)
      } catch (error) {
        this.$alertModal.showDanger({
          title: error.title,
          body: error.body
        })
      }

      this.gridApi?.hideOverlay()
    },
    getDatasource() {
      const dataSource = {
        rowCount: undefined,
        getRows: async (params: IGetRowsParams) => {
          this.gridApi?.showLoadingOverlay()

          let url = `${Appointment.ENDPOINT}`

          const urlParams = new URLSearchParams()
          urlParams.set('startRow', params.startRow)
          urlParams.set('endRow', params.endRow)

          if (params.sortModel.length) {
            urlParams.set('colId', params.sortModel[0].colId)
            urlParams.set('sort', params.sortModel[0].sort)
          }

          if (Object.keys(params.filterModel).length) {
            urlParams.set('query', params.filterModel['-'].filter)
          }

          if (this.appointmentsFilter) {
            urlParams.set('filter', this.appointmentsFilter)
          }

          const res = await this.$http.get<AppointmentPaginated>(url + `?${urlParams.toString()}`)
          this.rowData = res.data.map((appt) => Appointment.from(appt))
          this.gridApi?.hideOverlay()

          const lastRow = res.total
          // call the success callback
          params.successCallback(this.rowData, lastRow)
        }
      }
      return dataSource
    },

    async onGridReady(params: any) {
      this.gridApi = params.api
      if (this.selectedValue == 'quickView') {
        this.gridApi?.setGridOption('datasource', this.getDatasource())
        this.gridApi?.setGridOption('serverSideSortOnServer', true)
      } else if (this.selectedValue == 'allItems') {
        this.gridApi?.setGridOption('serverSideSortOnServer', false)
        this.updateRowData()
      } else if (this.selectedValue == 'deletedItems') {
        this.gridApi?.setGridOption('serverSideSortOnServer', false)
        await this.updateRowData(true)
      }
    },
    async delete(Appointments: Appointment[]) {
      if (confirm(this.$t('views.common.listView.selectedRows.deleteConfirm'))) {
        try {
          await this.$http.delete(Appointment.ENDPOINT, {data: Appointments})
          this.$alertModal.showSuccess({
            title: this.$t('views.common.listView.selectedRows.deleteSuccess')
          })
        } catch (error) {
          this.$alertModal.showDanger({
            title: error.title,
            body: error.body
          })
        }
      }
    },
    async restoreItems(Appointments: Appointment[]) {
      if (confirm(this.$t('views.common.listView.selectedRows.restoreConfirm'))) {
        try {
          await this.$http.post('/helpers/restore-deleted', {
            data: Appointments,
            table: 'customers_appointments'
          })
          this.$alertModal.showSuccess({
            title: this.$t('views.common.listView.selectedRows.restoreSuccess')
          })
        } catch (error) {
          this.$alertModal.showDanger({
            title: error.title,
            body: error.body
          })
        }
      }
    },
    filterResult() {
      if (this.rowModelType == 'infinite') {
        this.gridApi?.setFilterModel({'-': {filter: this.searchTerm}})
        return
      }
      this.gridApi?.setQuickFilter(this.searchTerm)
    },
    onCellDoubleClicked(event: any) {
      this.$router.push(
          this.$Route.APPOINTMENTS_APPOINTMENT_VIEW.replace(':appointment_id', event.data.id)
      )
    }
  }
})
</script>

<style></style>
